import { onPageScroll, pageScrollTop, prefersReducedMotion, storage } from '@lucid/core';

const SECTION_ID = 'lucid-locale';
const HINT_KEY = 'lucid:locale-hint';
const HINT_QUIET_DAYS = 30;
const HINT_DELAY = 2500;
const HINT_COMPACT_AFTER = 6000;
const HINT_COMPACT_SCROLL = 24;
const BOTTOM_BARS = '.lucid-buybar, .lucid-floating-bar';

/** @type {Map<string, Promise<Document | null>>} */
const requests = new Map();

function rootUrl() {
  return window.Shopify?.routes?.root || '/';
}

/** Fetches the picker section once per page view. */
export function fetchPicker() {
  const url = new URL(rootUrl(), window.location.origin);
  url.searchParams.set('section_id', SECTION_ID);
  const key = url.toString();
  if (!requests.has(key)) {
    const request = fetch(key, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((html) => new DOMParser().parseFromString(html, 'text/html'))
      .catch(() => {
        requests.delete(key);
        return null;
      });
    requests.set(key, request);
  }
  return requests.get(key);
}

function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
    .toLowerCase()
    .trim();
}

/**
 * Asks the page to open the picker. The header handles it (panel on desktop, menu view on mobile); without a header
 * the trigger's inline fallback form is shown.
 * @param {HTMLElement | null} trigger
 */
export function openLocale(trigger = null) {
  const event = new CustomEvent('lucid:locale:open', { bubbles: true, cancelable: true, detail: { trigger } });
  document.dispatchEvent(event);
  if (event.defaultPrevented) return true;
  const fallback = trigger?.dataset.fallback ? document.getElementById(trigger.dataset.fallback) : null;
  if (fallback) {
    fallback.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    fallback.querySelector('select')?.focus();
  }
  return false;
}

/** The picker: loads the localization form on demand and drives search, selection and languages. */
class LucidLocale extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  form = null;
  #loading = null;
  #rows = [];

  get loaded() {
    return Boolean(this.form);
  }

  load() {
    if (this.form) return Promise.resolve(true);
    if (this.#loading) return this.#loading;
    this.setAttribute('aria-busy', 'true');
    this.#loading = fetchPicker().then((doc) => {
      this.removeAttribute('aria-busy');
      this.#loading = null;
      const form = doc?.querySelector('form[data-locale-form]');
      if (!form) {
        this.classList.add('is-failed');
        return false;
      }
      const node = /** @type {HTMLFormElement} */ (document.importNode(form, true));
      this.replaceChildren(node);
      this.#bind(node);
      this.dispatchEvent(new CustomEvent('lucid:locale:ready', { bubbles: true }));
      return true;
    });
    return this.#loading;
  }

  /** Focus target after opening: the search field on pointer devices, the selected country otherwise. */
  focusStart({ preferSearch = true } = {}) {
    if (!this.form) return;
    const search = /** @type {HTMLInputElement | null} */ (this.form.querySelector('[data-locale-search]'));
    const checked = /** @type {HTMLInputElement | null} */ (this.form.querySelector('input[name="country_code"]:checked'));
    const target = preferSearch && search ? search : checked || search;
    target?.focus({ preventScroll: true });
    if (target === checked) checked?.closest('label')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  reset() {
    if (!this.form) return;
    const search = /** @type {HTMLInputElement | null} */ (this.form.querySelector('[data-locale-search]'));
    if (search && search.value) {
      search.value = '';
      this.#filter('');
    }
    const scroller = this.form.querySelector('[data-locale-scroll]');
    if (scroller) scroller.scrollTop = 0;
  }

  /** @param {HTMLFormElement} form */
  #bind(form) {
    this.form = form;
    this.#rows = [...form.querySelectorAll('.lucid-locale__row:not([data-twin])')].map((row) => ({
      row,
      q: normalize(row.getAttribute('data-q')),
    }));

    form.querySelector('[data-locale-search]')?.addEventListener('input', (event) => {
      this.#filter(normalize(/** @type {HTMLInputElement} */ (event.target).value));
    });
    form.querySelector('[data-locale-search]')?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      const first = this.#rows.find(({ row }) => !row.hidden)?.row.querySelector('input');
      if (first) {
        first.checked = true;
        this.#onCountry(first);
        first.focus();
      }
    });
    form.addEventListener('change', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      if (input.name === 'country_code') this.#onCountry(input);
      if (input.name === 'language_code') this.#summary();
    });
    form.addEventListener('submit', (event) => {
      const country = form.querySelector('input[name="country_code"]:checked')?.value || form.dataset.currentCountry;
      const language =
        form.querySelector('input[name="language_code"]:checked')?.value ||
        form.querySelector('input[name="language_code"]')?.value ||
        form.dataset.currentLanguage;
      if (country === form.dataset.currentCountry && language === form.dataset.currentLanguage) {
        event.preventDefault();
        this.dispatchEvent(new CustomEvent('lucid:locale:close', { bubbles: true }));
        return;
      }
      const back = form.querySelector('input[name="return_to"]');
      if (back) back.value = window.location.pathname + window.location.search;
      form.querySelector('[type="submit"]')?.setAttribute('aria-busy', 'true');
    });

    const checked = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="country_code"]:checked'));
    if (checked) this.#onCountry(checked, false);
  }

  /** @param {string} query */
  #filter(query) {
    const form = this.form;
    if (!form) return;
    let hits = 0;
    for (const { row, q } of this.#rows) {
      const match = !query || q.includes(query);
      row.hidden = !match;
      if (match) hits += 1;
    }
    const searching = query.length > 0;
    for (const el of form.querySelectorAll('[data-locale-group], [data-locale-pinned]')) {
      /** @type {HTMLElement} */ (el).hidden = searching;
    }
    const empty = /** @type {HTMLElement | null} */ (form.querySelector('[data-locale-empty]'));
    if (empty) empty.hidden = hits > 0;
    const scroller = form.querySelector('[data-locale-scroll]');
    if (scroller) scroller.scrollTop = 0;
  }

  /**
   * @param {HTMLInputElement} input
   * @param {boolean} [userChange]
   */
  #onCountry(input, userChange = true) {
    const form = this.form;
    if (!form) return;
    const code = input.value;
    for (const radio of form.querySelectorAll('input[name="country_code"]')) {
      radio.closest('label')?.classList.toggle('is-selected', /** @type {HTMLInputElement} */ (radio).value === code);
    }

    const langs = (input.dataset.langs || '').split(' ').filter(Boolean);
    const group = /** @type {HTMLElement | null} */ (form.querySelector('[data-locale-langs]'));
    if (group && langs.length) {
      const options = [...group.querySelectorAll('input[name="language_code"]')].map(
        (radio) => /** @type {HTMLInputElement} */ (radio)
      );
      for (const radio of options) {
        const label = /** @type {HTMLElement} */ (radio.closest('label'));
        const available = langs.includes(radio.value);
        label.hidden = !available;
        radio.disabled = !available;
      }
      const current = options.find((radio) => radio.checked && !radio.disabled);
      if (!current) {
        const preferred = options.find((radio) => radio.value === langs[0]) || options.find((radio) => !radio.disabled);
        if (preferred) preferred.checked = true;
      }
      const seg = /** @type {HTMLElement | null} */ (group.querySelector('.lucid-locale__seg'));
      const note = /** @type {HTMLElement | null} */ (group.querySelector('[data-locale-note]'));
      const single = options.filter((radio) => !radio.disabled).length < 2;
      if (seg) {
        seg.hidden = single;
        seg.style.setProperty('--lc-locale-n', String(Math.max(1, langs.length)));
      }
      if (note) {
        note.hidden = !single;
        const chosen = options.find((radio) => radio.checked);
        note.textContent = (form.dataset.note || '')
          .replace('[country]', input.dataset.name || '')
          .replace('[language]', chosen?.dataset.name || '');
      }
    }
    this.#summary(input);
    if (userChange) input.closest('label')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /** @param {HTMLInputElement | null} [countryInput] */
  #summary(countryInput = null) {
    const form = this.form;
    if (!form) return;
    const country =
      countryInput || /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="country_code"]:checked'));
    const language = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="language_code"]:checked'));
    const text = form.querySelector('[data-locale-summary-text]');
    const flag = /** @type {HTMLImageElement | null} */ (form.querySelector('[data-locale-summary-flag]'));
    if (!country || !text) return;
    const parts = [country.dataset.name, country.dataset.cur];
    if (language?.dataset.name) parts.push(language.dataset.name);
    text.textContent = parts.filter(Boolean).join(' · ');
    const rowFlag = /** @type {HTMLImageElement | null} */ (country.closest('label')?.querySelector('img'));
    if (flag && rowFlag) flag.src = rowFlag.currentSrc || rowFlag.src;
  }
}

/**
 * First-visit hint: when Shopify's browsing context suggests another country or language than the active one,
 * a glass bottom sheet offers the switch. It folds to one line after a few seconds or on scroll, sits above bottom
 * bars and is hidden by CSS while a menu, drawer or dialog is open. Dismissal is remembered for 30 days.
 */
class LucidLocaleHint extends HTMLElement {
  #card = null;
  #offScroll = null;
  #raf = 0;
  #timer = 0;
  #startTop = 0;

  connectedCallback() {
    if (window.Shopify?.designMode) return;
    const quiet = storage.getJSON(HINT_KEY, null);
    if (quiet && Date.now() - Number(quiet.t || 0) < HINT_QUIET_DAYS * 864e5) return;
    const start = () => window.setTimeout(() => this.#run(), HINT_DELAY);
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  disconnectedCallback() {
    this.#offScroll?.();
    clearTimeout(this.#timer);
    window.removeEventListener('resize', this.#place);
    this.#card?.remove();
  }

  async #run() {
    const country = this.dataset.country || '';
    const language = this.dataset.language || '';
    const url = new URL(`${rootUrl()}browsing_context_suggestions.json`, window.location.origin);
    url.searchParams.set('country[enabled]', 'true');
    url.searchParams.set('country[exclude]', country);
    url.searchParams.set('language[enabled]', 'true');
    url.searchParams.set('language[exclude]', language);

    let suggestion = null;
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return;
      const data = await response.json();
      suggestion = data?.suggestions?.[0]?.parts || null;
    } catch {
      return;
    }
    if (!suggestion) return;

    const doc = await fetchPicker();
    const form = doc?.querySelector('form[data-locale-form]');
    if (!form) return;
    const find = (code) =>
      /** @type {HTMLInputElement | null} */ (form.querySelector(`input[name="country_code"][value="${CSS.escape(code)}"]`));

    const suggestedCountry = suggestion.country?.handle?.toUpperCase();
    const suggestedLanguage = suggestion.language?.handle?.toLowerCase();
    const languageName = (code) =>
      /** @type {HTMLInputElement | null} */ (form.querySelector(`input[name="language_code"][value="${CSS.escape(code)}"]`))
        ?.dataset.name || '';

    let target = null;
    if (suggestedCountry && suggestedCountry !== country) {
      const input = find(suggestedCountry);
      if (input) {
        const langs = (input.dataset.langs || '').split(' ').filter(Boolean);
        let lang = langs[0] || language;
        if (suggestedLanguage && langs.includes(suggestedLanguage)) lang = suggestedLanguage;
        else if (langs.includes(language)) lang = language;
        target = { kind: 'country', country: suggestedCountry, language: lang, input };
      }
    }
    if (!target && suggestedLanguage && suggestedLanguage !== language) {
      const input = find(country);
      const langs = (input?.dataset.langs || '').split(' ').filter(Boolean);
      if (input && langs.includes(suggestedLanguage) && languageName(suggestedLanguage)) {
        target = { kind: 'language', country, language: suggestedLanguage, input };
      }
    }
    if (!target) return;

    const header = document.querySelector('lucid-header');
    if (header?.dataset.open) {
      window.setTimeout(() => this.#run(), 4000);
      return;
    }
    this.#show(target, languageName(target.language), form);
  }

  /**
   * @param {{ kind: string, country: string, language: string, input: HTMLInputElement }} target
   * @param {string} languageLabel
   * @param {Element} sourceForm
   */
  #show(target, languageLabel, sourceForm) {
    const tpl = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template'));
    if (!tpl) return;
    const card = /** @type {HTMLElement} */ (tpl.content.firstElementChild?.cloneNode(true));
    if (!card) return;
    const name = target.input.dataset.name || target.country;
    const fill = (value) =>
      String(value || '')
        .replace('[country]', name)
        .replace('[currency]', (target.input.dataset.cur || '').split(' ')[0])
        .replace('[language]', languageLabel);

    const title = card.querySelector('[data-hint-title]');
    const text = card.querySelector('[data-hint-text]');
    if (title) title.textContent = fill(this.dataset[target.kind === 'country' ? 'titleCountry' : 'titleLanguage']);
    if (text) {
      const copy = fill(this.dataset[target.kind === 'country' ? 'textCountry' : 'textLanguage']);
      text.textContent = copy;
      text.hidden = !copy;
    }
    const stay = card.querySelector('[data-hint-stay]');
    if (stay && target.kind === 'language' && this.dataset.stayLanguage) {
      (stay.querySelector('.lucid-btn__label') || stay).textContent = this.dataset.stayLanguage;
    }

    const flag = /** @type {HTMLImageElement | null} */ (card.querySelector('[data-hint-flag]'));
    const rowFlag = target.input.closest('label')?.querySelector('img');
    if (flag && rowFlag && target.kind === 'country') flag.src = rowFlag.getAttribute('src') || '';
    else flag?.remove();

    const form = /** @type {HTMLFormElement | null} */ (card.querySelector('form'));
    if (form) {
      const action = sourceForm.getAttribute('action');
      if (action) form.setAttribute('action', action);
      const set = (field, value) => {
        const input = /** @type {HTMLInputElement | null} */ (form.querySelector(`[name="${field}"]`));
        if (input) input.value = value;
      };
      set('country_code', target.country);
      set('language_code', target.language);
      form.addEventListener('submit', () => {
        set('return_to', window.location.pathname + window.location.search);
        this.#remember('switch');
        form.querySelector('[type="submit"]')?.setAttribute('aria-busy', 'true');
      });
    }

    const dismiss = () => {
      this.#remember('stay');
      this.#hide();
    };
    card.querySelector('[data-hint-stay]')?.addEventListener('click', dismiss);
    card.querySelector('[data-hint-close]')?.addEventListener('click', dismiss);
    card
      .querySelector('[data-hint-toggle]')
      ?.addEventListener('click', () => this.#setCompact(!card.classList.contains('is-compact'), true));
    card.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') dismiss();
    });
    card.addEventListener('pointerenter', () => clearTimeout(this.#timer));
    card.addEventListener('focusin', () => clearTimeout(this.#timer));

    document.body.append(card);
    this.#card = card;
    this.#startTop = pageScrollTop();
    this.#place();
    this.#offScroll = onPageScroll(() => {
      if (this.#raf) return;
      this.#raf = requestAnimationFrame(() => {
        this.#raf = 0;
        if (Math.abs(pageScrollTop() - this.#startTop) > HINT_COMPACT_SCROLL) this.#setCompact(true);
        this.#place();
      });
    });
    window.addEventListener('resize', this.#place);
    this.#timer = window.setTimeout(() => {
      if (!card.matches(':hover, :focus-within')) this.#setCompact(true);
    }, HINT_COMPACT_AFTER);
    requestAnimationFrame(() => card.classList.add('is-in'));
  }

  /**
   * @param {boolean} compact
   * @param {boolean} [byUser]
   */
  #setCompact(compact, byUser = false) {
    const card = this.#card;
    if (!card || card.classList.contains('is-compact') === compact) return;
    clearTimeout(this.#timer);
    if (!compact) this.#startTop = pageScrollTop();
    if (compact && !byUser && card.contains(document.activeElement)) return;
    card.classList.toggle('is-compact', compact);
    card.querySelector('[data-hint-toggle]')?.setAttribute('aria-expanded', String(!compact));
    const more = /** @type {HTMLElement | null} */ (card.querySelector('[data-hint-more]'));
    if (more) more.inert = compact;
  }

  /** Lifts the sheet above a visible fixed bottom bar (buy bar, floating filter bar). */
  #place = () => {
    const card = this.#card;
    if (!card) return;
    const height = window.innerHeight;
    let lift = 0;
    for (const bar of document.querySelectorAll(BOTTOM_BARS)) {
      if (!(bar instanceof HTMLElement) || bar.contains(card)) continue;
      const style = getComputedStyle(bar);
      if (style.position !== 'fixed' || style.visibility === 'hidden' || Number(style.opacity) < 0.1) continue;
      const rect = bar.getBoundingClientRect();
      if (rect.height === 0 || rect.top >= height - 1 || rect.bottom < height - 120) continue;
      lift = Math.max(lift, Math.round(height - rect.top));
    }
    card.style.setProperty('--lc-hint-lift', `${lift}px`);
  };

  #remember(choice) {
    storage.setJSON(HINT_KEY, { t: Date.now(), c: choice });
  }

  #hide() {
    const card = this.#card;
    if (!card) return;
    this.#card = null;
    clearTimeout(this.#timer);
    this.#offScroll?.();
    window.removeEventListener('resize', this.#place);
    card.classList.remove('is-in');
    window.setTimeout(() => card.remove(), prefersReducedMotion() ? 0 : 320);
  }
}

if (!customElements.get('lucid-locale')) customElements.define('lucid-locale', LucidLocale);
if (!customElements.get('lucid-locale-hint')) customElements.define('lucid-locale-hint', LucidLocaleHint);

document.addEventListener('click', (event) => {
  const trigger = /** @type {HTMLElement | null} */ (
    /** @type {HTMLElement} */ (event.target).closest?.('[data-lucid-locale-open]')
  );
  if (!trigger) return;
  event.preventDefault();
  openLocale(trigger);
});
